import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/events/event-soir.png";

const ironmanFranceNiceEn: ChauffeurPage = {
  slug: "ironman-france-nice",
  title: "Private chauffeur Ironman France Nice – VTC & transfers",
  subtitle:
    "Private chauffeur during Ironman France in Nice: early start on the Baie des Anges, access despite closed roads, airport transfers and supporter runs.",
  keywords: [
    "ironman nice chauffeur",
    "ironman france nice private driver",
    "nice triathlon chauffeur",
  ],
  hero: {
    title: "Private chauffeur during Ironman France in Nice",
    subtitle:
      "Swim in the Baie des Anges, bike in the hinterland, marathon on the Promenade: a dedicated chauffeur when roads close and start times are early.",
    keywords: [
      "Ironman France Nice chauffeur",
      "Nice triathlon VTC",
      "Nice airport Ironman transfer",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Private chauffeur during Ironman France in Nice",
    badges: ["Early starts", "Airport welcome", "Supporter runs"],
  },
  benefits: {
    heading: "Why a chauffeur during Ironman France",
    items: [
      {
        title: "Closed roads handled",
        description:
          "The Promenade des Anglais and hinterland roads close on race day. The chauffeur knows the access points to the start and the viewing spots.",
      },
      {
        title: "Very early starts",
        description:
          "The swim sets off early on the Baie des Anges: the chauffeur collects you on time, cold, with no parking stress.",
      },
      {
        title: "Airport minutes away",
        description:
          "Nice Côte d'Azur is less than 7 km away. Flight tracking, name-board welcome and a direct transfer with your luggage and gear.",
      },
      {
        title: "5.0/5 from 38 Google reviews",
        description:
          "Punctuality, discretion and immaculate vehicles: the consistency that earns KS Rent Services a 5.0/5 rating from 38 Google reviews.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services during Ironman France in Nice",
    services: [
      {
        title: "Early transfer to the start",
        text: "Drop-off near the Baie des Anges for the swim entry, early in the morning, at the agreed time.",
      },
      {
        title: "Nice airport meet-and-greet",
        text: "Airport ↔ hotel transfer with flight tracking, minutes from the Promenade des Anglais.",
      },
      {
        title: "Supporter runs",
        text: "We take families and supporters to key points on the bike and marathon courses, within the open roads.",
      },
      {
        title: "Full-day hire",
        text: "A dedicated chauffeur for a long race day, from the start to the finish and recovery.",
      },
      {
        title: "Team & group shuttles",
        text: "Multi-vehicle coordination for athletes, supporters and luggage.",
      },
    ],
  },
  locations: {
    heading: "Key points during Ironman France",
    items: [
      {
        name: "Baie des Anges / Promenade des Anglais",
        description: "Swim start and marathon finish on the seafront.",
      },
      {
        name: "Nice hinterland",
        description: "Roads and climbs of the bike course, viewing spots.",
      },
      {
        name: "Nice Côte d'Azur airport",
        description: "Less than 7 km away, Terminals 1 and 2.",
      },
      {
        name: "Old Nice & hotels",
        description: "Central and seafront lodging, close to the start.",
      },
      {
        name: "Finish & recovery area",
        description:
          "Pick-up after the effort, a direct transfer to the hotel.",
      },
    ],
  },
  generic: {
    heading: "Ironman France in Nice from a chauffeur's seat",
    subheading:
      "Every year, Nice hosts a long-distance triathlon: a swim in the Baie des Anges, a bike leg in the hinterland and a marathon on the Promenade des Anglais.",
    paragraphs: [
      "Every year, Ironman France transforms Nice for a long race day. The swim sets off early in the morning in the Baie des Anges, the bike course climbs into the Nice hinterland, and the marathon runs along the Promenade des Anglais. Depending on the edition, the race is held in early summer or in autumn.",
      "On race day, the Promenade and hinterland roads close. A chauffeur who knows the access points takes you to the start on time and drives your supporters to the best viewing spots, without getting lost in the diversions.",
      "Nice Côte d'Azur airport is less than seven kilometres away. With flight tracking and a name-board welcome, your transfer runs without waiting, luggage and gear included, both on arrival and on the way back after the race.",
    ],
    bullets: [
      "Drop-off near the start",
      "Course-following for supporters",
      "Airport under 7 km",
    ],
  },
  process: {
    heading: "Booking a chauffeur for Ironman France in Nice",
    steps: [
      "Tell us your hotel, your start time and the number of supporters to carry.",
      "We plan the routes around the closed roads on the Promenade and in the hinterland.",
      "The chauffeur arrives on time for the early start, with flight tracking for your arrivals.",
      "Direct transfer or hourly hire to cover the start, the course and recovery.",
    ],
  },
  faq: [
    {
      q: "How do I book a private chauffeur during Ironman France in Nice?",
      a: "Book ahead: on race weekend, Nice is in high demand and start times are early. Share your hotel, your start time and the number of supporters so we can secure a dedicated chauffeur.",
    },
    {
      q: "Can the chauffeur take me to the start early in the morning?",
      a: "Yes. The swim sets off very early on the Baie des Anges: the chauffeur collects you at the agreed time and drops you as close as possible to the water entry, with no parking worries.",
    },
    {
      q: "How do you handle the closed roads on race day?",
      a: "The Promenade des Anglais and hinterland roads are closed during the race. The chauffeur knows the diversions and authorised access points to reach the start and the viewing spots without getting lost.",
    },
    {
      q: "Can you drive my supporters along the course?",
      a: "Yes. We take families and supporters to key points on the bike and marathon courses, within the open roads, so you can cheer from several places during the day.",
    },
    {
      q: "Do you handle the airport transfer with gear?",
      a: "Yes, with flight tracking and a name-board welcome. Nice Côte d'Azur airport is less than 7 km away: we provide a vehicle sized for your luggage and gear, both on arrival and departure.",
    },
    {
      q: "Do you cover recovery after the race?",
      a: "Yes. After a long day of effort, the chauffeur collects you at the finish and drives you straight to the hotel, with no wait and no extra walking.",
    },
    {
      q: "When does Ironman France in Nice take place?",
      a: "The race is held in Nice every year; depending on the edition, it takes place in early summer or in autumn. Send us your exact dates and we organise your transfers around the race.",
    },
  ],
  relatedLinks: [
    {
      label: "Nice chauffeur rates",
      href: "/en/chauffeur/nice",
    },
    {
      label: "Nice Airport shuttle",
      href: "/en/chauffeur/airport",
    },
    {
      label: "Car service on the French Riviera",
      href: "/en/chauffeur/french-riviera",
    },
    {
      label: "Car rental with delivery in Nice",
      href: "/en/location/car-rental-nice",
    },
  ],
  cta: {
    title: "Your chauffeur for Ironman France in Nice",
    text: "Early start, access despite closed roads and supporter runs: book a dedicated chauffeur for your race weekend.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    title: "Ironman France Nice Chauffeur – car service | KS Rent",
    description:
      "Private chauffeur during Ironman France in Nice: early start on the Baie des Anges, access despite closed roads, airport transfer and supporter runs. Book now.",
    canonical: "/en/chauffeur/ironman-france-nice",
  },
};

export default ironmanFranceNiceEn;
